// packages/web/src/routes/_authed/work/changes.tsx
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { IndexPageHeader, IndexPageHeaderTitle } from "@/components/vroom/index-page-header";
import { Badge } from "@/components/ui/badge";
import { changesOptions } from "@/api/change-log/query-options";
import { isoWeekFromDate, isoWeekRange } from "@/lib/temporal";

export const Route = createFileRoute("/_authed/work/changes")({
  validateSearch: (s: Record<string, unknown>) => ({
    week: typeof s.week === "string" ? s.week : isoWeekFromDate(new Date()),
  }),
  component: ChangesPage,
});

function shiftWeek(weekISO: string, delta: number): string {
  const m = weekISO.match(/^(\d{4})-W(\d{2})$/);
  if (!m) throw new Error("Invalid weekISO");
  const { fromDate } = isoWeekRange(Number(m[1]), Number(m[2]));
  const next = new Date(`${fromDate}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + delta * 7);
  return isoWeekFromDate(next);
}

function ChangesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { week } = Route.useSearch();
  const { user } = Route.useRouteContext() as { user: { id: string } };
  const q = useQuery(changesOptions(user.id, week));

  return (
    <>
      <IndexPageHeader>
        <IndexPageHeaderTitle
          title={t("work.changes.title")}
          kicker={week}
          onPrev={() =>
            void navigate({ to: "/work/changes", search: { week: shiftWeek(week, -1) } })
          }
          onNext={() =>
            void navigate({ to: "/work/changes", search: { week: shiftWeek(week, +1) } })
          }
        />
      </IndexPageHeader>
      <div className="space-y-4 px-4 py-4 md:px-6 md:py-6">
        {(q.data ?? []).length === 0 ? (
          <p className="text-ink-500">{t("work.changes.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {q.data!.map((r) => (
              <li key={r.id} className="rounded-4 border-line border bg-surface p-3">
                <div className="flex items-center gap-2">
                  <Badge variant="default">{r.changeType}</Badge>
                  <span className="text-xs text-ink-500">
                    {r.entityType} · {new Date(r.createdAt).toLocaleString("nl-NL")}
                  </span>
                </div>
                <pre className="text-xs mt-2 whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify({ before: r.before, after: r.after }, null, 2)}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
